import { message } from "antd";
import { jwtDecode } from "jwt-decode";

function getCookieValue(name) {
  const regex = new RegExp(`(^| )${name}=([^;]+)`)
  const match = document.cookie.match(regex)
  if (match) {
    return match[2]
  }
}

export function getWorkWiseAuthToken() {
  const searchParams = new URLSearchParams(document.location.search);
  let cookieToken = getCookieValue("token")
  let queryToken = searchParams.get("token");
  // No fallback: without a token the document route ends up on /unauthorized.
  const finalToken = queryToken || cookieToken || null;
  return finalToken;
}

export const getUserInfoFromToken = () => {
  try {
    const token = getWorkWiseAuthToken();

    if (!token) return null;

    const decoded = jwtDecode(token);

    return decoded || null;
  } catch (error) {
    console.error("Invalid token:", error);
    return null;
  }
};

export function decodeWorkWiseToken() {
  const token = getWorkWiseAuthToken();
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
}

/**
 * Manual "work offline" mode.
 *
 * localStorage is the source of truth so non-React code (the API guard, the
 * socket guard) can read it synchronously; the document context mirrors it for
 * rendering.
 */
const MANUAL_OFFLINE_KEY = 'milegridManualOffline';

export const getManualOfflineMode = () => {
  try {
    return localStorage.getItem(MANUAL_OFFLINE_KEY) === 'true';
  } catch (error) {
    return false;
  }
};

export const setManualOfflineMode = (enabled) => {
  try {
    localStorage.setItem(MANUAL_OFFLINE_KEY, enabled ? 'true' : 'false');
  } catch (error) {
    console.error('Error persisting offline mode:', error);
  }
};

/** Offline for our purposes: no network OR the user chose to work offline. */
export const isEffectivelyOffline = () =>
  !window.navigator.onLine || getManualOfflineMode();

export function getSelectedData(sheet, selectedRange) {
  const { sri, sci, eri, eci } = selectedRange;
  const data = [];
  const headers = [];
  for (let ci = sci; ci <= eci; ci += 1) {
    const column = [];
    for (let ri = sri; ri <= eri; ri += 1) {
      const cell = sheet.data.getCell(ri, ci);
      const cellData = cell && typeof cell.text === 'string' ? cell.text.trim() : null;
      if (ri === sri) {
        headers.push(cellData || "");
        column.push(cellData || "");
      } else {
        column.push(cellData || "");
      }
    }
    if (column.length > 0) {
      data.push(column);
    }
  }
  let payload = {
    data: data,
    headers: headers
  }
  return payload;
}

function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Next sheet name like "Template N" where N is one greater than the highest
 * existing numbered suffix (and a bare "Template" counts as using slot 1).
 */
export function findSheetName(sheets, text) {
  const safeSheets = Array.isArray(sheets) ? sheets : [];
  const sheetNames = [...new Set(safeSheets.map((sheet) => sheet?.name).filter(Boolean))];

  const escaped = escapeRegExp(text);
  const numberedPattern = new RegExp(`^${escaped}\\s+(\\d+)$`);

  let maxSuffix = 0;
  let hasExactBase = false;

  for (const name of sheetNames) {
    if (name === text) {
      hasExactBase = true;
      continue;
    }
    const match = name.match(numberedPattern);
    if (match) {
      const n = parseInt(match[1], 10);
      if (!Number.isNaN(n)) maxSuffix = Math.max(maxSuffix, n);
    }
  }

  const effectiveMax = Math.max(maxSuffix, hasExactBase ? 1 : 0);
  const next = effectiveMax + 1;
  return `${text} ${next}`;
}
export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";

  const units = ["Bytes", "KB", "MB", "GB"];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  if (unitIndex === 0) {
    return `${size} ${units[unitIndex]}`;
  } else if (unitIndex === 1 && size < 50) {
    return `${parseFloat(size.toFixed(0))} ${units[unitIndex]}`;
  } else {
    return `${parseFloat(size.toFixed(2))} ${units[unitIndex]}`;
  }
};
export const getWatermarkData = async () => {
  const now = new Date();
  const currentDateTime = now.toLocaleString();

  // Get IP address
  let ip = 'N/A';
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    ip = data.ip;
  } catch (error) {
    console.error("Error fetching IP:", error);
    message.error("Could not fetch IP address");
  }

  // Get location
  let location = { lat: 'N/A', lng: 'N/A' };
  try {
    location = await new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        message.error("Geolocation not supported");
        return resolve(location);
      }

      navigator.geolocation.getCurrentPosition(
        (position) => resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        }),
        (error) => {
          handleGeolocationError(error);
          resolve(location); // Return default on error
        }
      );
    });
  } catch (error) {
    console.error("Location error:", error);
  }

  return {
    currentDateTime,
    ip,
    location
  };
};

const handleGeolocationError = (error) => {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      message.error("Location access denied. Please enable permissions.");
      break;
    case error.POSITION_UNAVAILABLE:
      message.error("Location information unavailable.");
      break;
    case error.TIMEOUT:
      message.error("Location request timed out.");
      break;
    default:
      message.error("Unknown location error occurred.");
  }
};

export const processTemplateData = (serverResponse) => {
  let templateData = serverResponse?.data?.data || null;
  if (templateData?.milepadData) {
    try {
      const rawData = JSON.parse(templateData.milepadData);
      delete templateData.milepadData;
      templateData.spreadsheetData = rawData;
    } catch (error) {
      console.error('Failed to parse template milepadData:', error);
      delete templateData.milepadData;
      templateData.spreadsheetData = [];
    }
  }
  return templateData;
};